import { useEffect, useState } from 'react';

interface CodeSnippet {
  id: number;
  x: number;
  y: number;
  code: string;
  delay: number;
  duration: number;
  opacity: number;
}

const codeExamples = [
  'const app = createApp();',
  'npm run build',
  'git push origin main',
  'async function fetchData() {',
  'export default Component;',
  'yarn dev --turbo',
  'docker compose up',
  'pnpm install',
  'interface User { }',
  'return <App />;',
  'cargo build --release',
  'python manage.py',
  'go run main.go',
  'flutter build ios',
  'npx create-next-app',
];

export function DownloadBackground() {
  const [snippets, setSnippets] = useState<CodeSnippet[]>([]);

  useEffect(() => {
    const newSnippets: CodeSnippet[] = Array.from({ length: 15 }, (_, i) => ({
      id: i,
      x: Math.random() * 80 + 10,
      y: Math.random() * 80 + 10,
      code: codeExamples[i % codeExamples.length],
      delay: Math.random() * 10,
      duration: Math.random() * 5 + 8,
      opacity: Math.random() * 0.15 + 0.05,
    }));
    setSnippets(newSnippets);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Base gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-transparent to-purple-500/5" />

      {/* Animated gradient orbs */}
      <div
        className="absolute w-[800px] h-[800px] rounded-full"
        style={{
          left: '-15%',
          top: '-10%',
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 60%)',
          animation: 'orbFloat 20s ease-in-out infinite',
        }}
      />
      <div
        className="absolute w-[600px] h-[600px] rounded-full"
        style={{
          right: '-10%',
          top: '40%',
          background: 'radial-gradient(circle, rgba(168, 85, 247, 0.1) 0%, transparent 60%)',
          animation: 'orbFloat 15s ease-in-out infinite 5s',
        }}
      />
      <div
        className="absolute w-[500px] h-[500px] rounded-full"
        style={{
          left: '40%',
          bottom: '-15%',
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 60%)',
          animation: 'orbFloat 18s ease-in-out infinite 8s',
        }}
      />

      {/* Floating code snippets */}
      {snippets.map((snippet) => (
        <div
          key={snippet.id}
          className="absolute font-mono text-xs md:text-sm whitespace-nowrap"
          style={{
            left: `${snippet.x}%`,
            top: `${snippet.y}%`,
            opacity: snippet.opacity,
            color: 'rgba(139, 92, 246, 0.8)',
            animation: `codeFloat ${snippet.duration}s ease-in-out infinite ${snippet.delay}s`,
            textShadow: '0 0 20px rgba(139, 92, 246, 0.3)',
          }}
        >
          {snippet.code}
        </div>
      ))}

      {/* Speed lines - representing fast development */}
      <div className="absolute inset-0">
        {[...Array(8)].map((_, i) => (
          <div
            key={`line-${i}`}
            className="absolute h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent"
            style={{
              left: '0',
              right: '0',
              top: `${15 + i * 10}%`,
              animation: `speedLine 3s ease-in-out infinite ${i * 0.3}s`,
              transformOrigin: 'left center',
            }}
          />
        ))}
      </div>

      {/* Typing cursor effect */}
      <div
        className="absolute hidden lg:block"
        style={{
          left: '8%',
          top: '20%',
          animation: 'fadeInOut 4s ease-in-out infinite',
        }}
      >
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-bg-elevated/80 border border-border-default backdrop-blur-sm">
          <span className="text-success text-sm">$</span>
          <span className="text-text-secondary text-sm font-mono">ochtopus --init</span>
          <span className="w-2 h-4 bg-accent animate-pulse" />
        </div>
      </div>

      {/* Productivity stats floating */}
      <div
        className="absolute hidden lg:flex items-center gap-2 px-3 py-2 rounded-full bg-success/10 border border-success/20 backdrop-blur-sm"
        style={{
          right: '8%',
          top: '25%',
          animation: 'floatBadge 6s ease-in-out infinite',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-success">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
          <polyline points="17 6 23 6 23 12" />
        </svg>
        <span className="text-xs text-success font-medium">10x Daha Hızlı</span>
      </div>

      <div
        className="absolute hidden lg:flex items-center gap-2 px-3 py-2 rounded-full bg-accent/10 border border-accent/20 backdrop-blur-sm"
        style={{
          left: '12%',
          bottom: '25%',
          animation: 'floatBadge 6s ease-in-out infinite 2s',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-accent">
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
        </svg>
        <span className="text-xs text-accent font-medium">AI Destekli</span>
      </div>

      <div
        className="absolute hidden lg:flex items-center gap-2 px-3 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 backdrop-blur-sm"
        style={{
          right: '15%',
          bottom: '30%',
          animation: 'floatBadge 6s ease-in-out infinite 4s',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-purple-400">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
        <span className="text-xs text-purple-400 font-medium">Anında Kurulum</span>
      </div>

      {/* Code brackets decoration */}
      <div className="absolute left-[5%] top-[40%] text-6xl font-mono text-accent/10 select-none" style={{ animation: 'pulse 4s ease-in-out infinite' }}>
        {'{ }'}
      </div>
      <div className="absolute right-[5%] top-[50%] text-6xl font-mono text-purple-500/10 select-none" style={{ animation: 'pulse 4s ease-in-out infinite 2s' }}>
        {'< />'}
      </div>

      {/* Grid pattern */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.02]">
        <defs>
          <pattern id="downloadGrid" width="50" height="50" patternUnits="userSpaceOnUse">
            <path d="M 50 0 L 0 0 0 50" fill="none" stroke="rgba(139, 92, 246, 0.5)" strokeWidth="1"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#downloadGrid)" />
      </svg>

      {/* Animation keyframes */}
      <style>{`
        @keyframes orbFloat {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          50% {
            transform: translate(30px, 20px) scale(1.05);
          }
        }

        @keyframes codeFloat {
          0%, 100% {
            transform: translateY(0) translateX(0);
            opacity: inherit;
          }
          25% {
            transform: translateY(-15px) translateX(10px);
          }
          50% {
            transform: translateY(-5px) translateX(-5px);
            opacity: calc(inherit * 1.5);
          }
          75% {
            transform: translateY(-20px) translateX(5px);
          }
        }

        @keyframes speedLine {
          0% {
            transform: scaleX(0);
            opacity: 0;
          }
          50% {
            transform: scaleX(1);
            opacity: 0.5;
          }
          100% {
            transform: scaleX(0);
            opacity: 0;
          }
        }

        @keyframes floatBadge {
          0%, 100% {
            transform: translateY(0);
            opacity: 0.9;
          }
          50% {
            transform: translateY(-10px);
            opacity: 1;
          }
        }

        @keyframes fadeInOut {
          0%, 100% {
            opacity: 0.7;
          }
          50% {
            opacity: 1;
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 0.1;
            transform: scale(1);
          }
          50% {
            opacity: 0.15;
            transform: scale(1.05);
          }
        }
      `}</style>
    </div>
  );
}
